<template>
  <div>
    <Header></Header>
    <div class="col-12 d-inline-flex">
      <div class="col-10 no-overflow ms-3 me-3 pt-3" style="position: relative;">
        <h2>Calendars</h2>
        <p class="text-muted">
          Imported fixture calendars. Each one holds the matches parsed from an uploaded .ics file — configure
          their feeds from here, or upload a newer file to update an existing calendar's schedule.
        </p>
        <div class="d-flex justify-content-between align-items-center cal-leagues-toolbar">
          <div class="d-flex align-items-center cal-leagues-actions" style="gap: 0.75rem;">
            <a role="button" class="procricket-btn mb-3" @click="redirectToCreate">Import Calendar</a>
            <router-link :to="{ name: 'CalMatchesAll' }" class="procricket-btn--reverse mb-3">All Matches</router-link>
            <router-link :to="{ name: 'Daily' }" class="procricket-btn--reverse mb-3">Daily</router-link>
            <div class="view-switch mb-3" role="group" aria-label="Calendars to show">
              <a role="button" :class="{ active: calendarFilter === 'active' }" @click="calendarFilter = 'active'">Active</a>
              <a role="button" :class="{ active: calendarFilter === 'all' }" @click="calendarFilter = 'all'">All</a>
            </div>
          </div>
          <input type="text" class="form-control mb-3 match-search-input" placeholder="Search calendars..." v-model="searchText" style="width: 20%; position: relative;">
        </div>
        <div class="table-responsive">
          <table class="table table-striped table-bordered cal-leagues-table">
            <thead>
            <tr>
              <th class="cl-id" @click="toggleSort('calLeagueId')">Id</th>
              <th class="cl-name" @click="toggleSort('name')">Name</th>
              <th class="cl-short" @click="toggleSort('shortName')">Short Name</th>
              <th class="cl-sport" @click="toggleSort('sport')">Sport</th>
              <th class="cl-uid" @click="toggleSort('uid')">Calendar UID</th>
              <th class="cl-source" @click="toggleSort('calendarSourceUrl')">Source URL</th>
              <th class="cl-import" @click="toggleSort('lastImport')">Last Import</th>
              <th class="cl-status" @click="toggleSort('status')">Status</th>
              <th class="cl-active" @click="toggleSort('active')">Active</th>
              <th class="cl-btn">Matches</th>
              <th class="cl-btn">Match Events</th>
              <th class="cl-btn">Edit</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="config in sortData" :key="config.calLeagueId" class="league-summary-row">

              <td class="mobile-league-card" colspan="12">
                <div class="mobile-card-top">
                  <span class="mobile-card-name text-truncate">{{ config.name }}</span>
                  <span v-if="!config.active" class="badge bg-secondary">Inactive</span>
                </div>
                <div class="mobile-card-meta text-muted small">
                  <span v-if="config.shortName">{{ config.shortName }}</span>
                  <span v-if="config.shortName && config.sport"> &middot; </span>
                  <span>{{ config.sport }}</span>
                </div>
                <div class="mobile-card-meta text-muted small text-truncate">
                  UID: {{ truncateText(config.uid, 40) }}
                </div>
                <div v-if="config.calendarSourceUrl" class="mobile-card-meta text-muted small text-truncate">
                  Source: <a :href="config.calendarSourceUrl" target="_blank" rel="noopener noreferrer">{{ truncateText(config.calendarSourceUrl, 40) }}</a>
                </div>
                <div class="mobile-card-meta text-muted small">
                  {{ truncateText(config.lastImport, 30) }} &middot; {{ config.status }}
                </div>
                <div class="mobile-card-actions mt-2">
                  <router-link :to="{ name: 'CalLeagueMatches', params: { calLeagueId: config.calLeagueId, ...config } }" class="procricket-btn--reverse">
                    Matches
                  </router-link>
                  <router-link :to="{ name: 'CalLeagueMatchEvents', params: { calLeagueId: config.calLeagueId, ...config } }" class="procricket-btn--reverse">
                    Match Events
                  </router-link>
                  <router-link :to="{ name: 'CalLeagueEdit', params: { calLeagueId: config.calLeagueId, ...config } }" class="procricket-btn">
                    Edit
                  </router-link>
                </div>
              </td>
              <td class="text-truncate" v-html="truncateText(config.calLeagueId, 30)"></td>
              <td class="text-truncate" :title="config.name">{{ config.name }}</td>
              <td class="text-truncate" :title="config.shortName" v-html="truncateText(config.shortName, 20)"></td>
              <td class="text-truncate" :title="config.sport" v-html="truncateText(config.sport, 20)"></td>
              <td class="text-truncate" :title="config.uid">{{ config.uid }}</td>
              <td class="text-truncate">
                <a v-if="config.calendarSourceUrl" :href="config.calendarSourceUrl" target="_blank" rel="noopener noreferrer" :title="config.calendarSourceUrl" class="source-url-link"><bdi>{{ config.calendarSourceUrl }}</bdi></a>
                <span v-else>&mdash;</span>
              </td>
              <td class="text-truncate" :title="config.lastImport" v-html="truncateText(config.lastImport, 30)"></td>
              <td class="text-truncate" v-html="truncateText(config.status, 30)"></td>
              <td class="text-truncate" v-html="truncateText(config.active, 30)"></td>
              <td class="centered">
                <router-link :to="{ name: 'CalLeagueMatches', params: { calLeagueId: config.calLeagueId, ...config } }" class="procricket-btn--reverse">
                  Matches
                </router-link>
              </td>
              <td class="centered">
                <router-link :to="{ name: 'CalLeagueMatchEvents', params: { calLeagueId: config.calLeagueId, ...config } }" class="procricket-btn--reverse">
                  Match Events
                </router-link>
              </td>
              <td class="centered">
                <router-link :to="{ name: 'CalLeagueEdit', params: { calLeagueId: config.calLeagueId, ...config } }" class="procricket-btn">
                  Edit
                </router-link>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import AdminService from "@/services/admin-service";
import Header from "@/components/Header.vue";

export default {
  name: "CalLeagues",
  components: {
    Header,
  },
  data() {
    return {
      searchText: '',
      CalLeaguesList: [],
      sortColumn: '',
      sortDirection: 'asc',
      calendarFilter: 'active'
    };
  },
  computed: {
    filteredCalLeaguesList() {
      const search = this.searchText.toLowerCase().trim();
      let filteredList = this.CalLeaguesList.filter((config) => {
        if (this.calendarFilter === 'active' && !config.active) return false;
        return (
            (config.calLeagueId && config.calLeagueId.toString().includes(search)) ||
            (config.name && config.name.toString().toLowerCase().includes(search)) ||
            (config.shortName && config.shortName.toString().toLowerCase().includes(search)) ||
            (config.sport && config.sport.toString().toLowerCase().includes(search)) ||
            (config.uid && config.uid.toString().toLowerCase().includes(search)) ||
            (config.calendarSourceUrl && config.calendarSourceUrl.toString().toLowerCase().includes(search)) ||
            (config.status && config.status.toString().includes(search)) ||
            (config.active && config.active.toString().includes(search))
        );
      });

      if (this.sortColumn) {
        const direction = this.sortDirection === 'asc' ? 1 : -1;
        filteredList = filteredList.sort((a, b) => {
          const valueA = a[this.sortColumn];
          const valueB = b[this.sortColumn];

          if (valueA < valueB) return -1 * direction;
          if (valueA > valueB) return 1 * direction;
          return 0;
        });
      }
      return filteredList;
    },
    sortData() {
      return this.filteredCalLeaguesList;
    },
  },
  created() {
    this.doLoadCalLeagues();
    this.sortColumn = 'calLeagueId';
  },
  methods: {
    doLoadCalLeagues() {
      AdminService.getCalLeaguesList().then((data) => {
        this.CalLeaguesList = data;
      }).catch((error) => {
        console.error("Error loading calendars:", error);
      });
    },
    truncateText(text, limit) {
      if (text === undefined || text === null) {
        return null
      } else {
        return String(text).length > limit ? String(text).substring(0, limit) + '...' : String(text);
      }
    },
    redirectToCreate() {
      this.$router.push({ name: 'CalLeagueCreate' });
    },
    toggleSort(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        this.sortDirection = 'asc';
      }
    }
  },
};
</script>
<style src="../../assets/style.css">
</style>
